"use client";

/**
 * @module DialogScreens
 *
 * Animates between "screens" inside a single Dialog. Transitions run in
 * sequence — old screen slides out, the wrapper resizes its height to fit
 * the new screen, then the new screen slides in — so the surface never
 * morphs awkwardly when screens differ in height.
 *
 * Forward navigation slides the new screen in from the right; backward
 * navigation slides it in from the left. The slide uses a short travel
 * distance plus opacity so the motion reads as a soft push rather than a
 * full-width swipe.
 *
 * Use when a Dialog has multiple steps and you want a single continuous
 * surface (size + position smoothly interpolated) instead of swapping
 * separate Dialog instances.
 *
 * @example
 * // Two-screen auth dialog
 * const [screen, setScreen] = useState<"login" | "forgot">("login");
 * const [direction, setDirection] = useState<"forward" | "backward">("forward");
 *
 * const goForgot = () => { setDirection("forward"); setScreen("forgot"); };
 * const goBack   = () => { setDirection("backward"); setScreen("login"); };
 *
 * <DialogContent>
 *   <DialogScreens activeScreen={screen} direction={direction}>
 *     {screen === "login"  && <LoginScreen onForgot={goForgot} />}
 *     {screen === "forgot" && <ForgotScreen onBack={goBack} />}
 *   </DialogScreens>
 * </DialogContent>
 */
import { AnimatePresence, motion } from "motion/react";
import type * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { cn } from "../lib/utils";

/** Direction of the slide animation. */
export type DialogScreenDirection = "forward" | "backward";
export interface DialogScreensProps {
  /**
   * Identifier of the currently active screen. Changing this value triggers
   * the slide animation; the children rendered for the new value will slide
   * in while the previous children slide out.
   */
  activeScreen: string;
  /** Children to render for the active screen. */
  children: React.ReactNode;
  className?: string;
  /**
   * Direction the new screen should enter from. `"forward"` slides the
   * incoming screen in from the right (old slides off to the left).
   * `"backward"` does the opposite. Defaults to `"forward"`.
   */
  direction?: DialogScreenDirection;
  /** Duration of the container's height animation in seconds. Defaults to 0.14. */
  resizeDuration?: number;
  /** Duration of one slide leg (exit OR enter) in seconds. Defaults to 0.1. */
  slideDuration?: number;
}
const EASE_OUT_QUART: [number, number, number, number] = [0.32, 0.72, 0, 1];
const DialogScreens = ({
  activeScreen,
  direction = "forward",
  slideDuration = 0.1,
  resizeDuration = 0.14,
  children,
  className
}: DialogScreensProps) => {
  const sign = direction === "forward" ? 1 : -1;
  const [height, setHeight] = useState<number | "auto">("auto");
  const observerRef = useRef<ResizeObserver | null>(null);

  // Tracks the active screen's height. With `mode="wait"`, the new screen
  // only mounts after the old one finishes exiting, so the initial measure
  // fires exactly when the wrapper needs to start resizing toward the new
  // height — and framer-motion animates from the current height to the new
  // one. A ResizeObserver also follows post-mount growth (e.g. validation
  // errors, async-loaded content) so the wrapper never clips its child.
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (node) {
      const initial = node.scrollHeight;
      setHeight(prev => prev === initial ? prev : initial);
      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          const next = entry.contentRect.height;
          // Skip identical updates so framer-motion doesn't re-target the
          // height animation on every sub-pixel report during entry.
          setHeight(prev => prev === next ? prev : next);
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  // Delay the entrance slide so the height animation finishes first. Without
  // this, the dialog re-centers vertically *while* the new content is sliding
  // in horizontally, which reads as diagonal motion. With the delay, the
  // dialog first resizes (pure vertical), then content slides in (pure
  // horizontal).
  const slideVariants = useMemo(() => ({
    enter: (s: number) => ({
      x: `${s * 24}%`,
      opacity: 0
    }),
    center: {
      x: "0%",
      opacity: 1,
      transition: {
        duration: slideDuration,
        ease: EASE_OUT_QUART,
        delay: resizeDuration
      }
    },
    exit: (s: number) => ({
      x: `${-s * 24}%`,
      opacity: 0
    })
  }), [slideDuration, resizeDuration]);
  return <motion.div animate={{
    height
  }} className={cn("relative max-h-full overflow-x-hidden overflow-y-auto", className)} initial={false} transition={{
    duration: resizeDuration,
    ease: EASE_OUT_QUART
  }} data-sentry-element="motion.div" data-sentry-component="DialogScreens" data-sentry-source-file="dialog-screens.tsx">
      <AnimatePresence custom={sign} initial={false} mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="dialog-screens.tsx">
        <motion.div animate="center" custom={sign} exit="exit" initial="enter" key={activeScreen} ref={measureRef} transition={{
        duration: slideDuration,
        ease: EASE_OUT_QUART
      }} variants={slideVariants} data-sentry-element="motion.div" data-sentry-source-file="dialog-screens.tsx">
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>;
};
export { DialogScreens };